import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Field from '../../shared/components/fields/Inputs';
import { validate } from '../../utils.js';
import './wizard.css';

class Wizard extends Component {
    static propTypes = {
        record: PropTypes.object.isRequired,
        onSubmit: PropTypes.func.isRequired,
        errors: PropTypes.object
    }

    state = {
        data: { ...this.props.record.data },
        errors: this.props.saveErrors || {}, // { fd_key: [str_error_messages], ...}
        sectionIdx: 0
    }

    editSection = e => {
        let sects = this.getRD().sections;
        let navigatingTo = parseInt(e.target.dataset.idx);

        if (this.state.sectionIdx < sects.length && navigatingTo > this.state.sectionIdx) {
            let results = this.validateSection(sects[this.state.sectionIdx]);
            if (results.hasErrors)
            {
                this.focusFirstErrorField(results.errors);
                this.setState({errors: {...this.state.errors, ...results.errors}});
                return;
            }
        }

        this.setState({sectionIdx: navigatingTo});
    }

    showReview = e => {
        let sects = this.getRD().sections;
        if (this.state.sectionIdx < sects.length) {
            let results = this.validateSection(sects[this.state.sectionIdx]);
            if (results.hasErrors)
            {
                this.focusFirstErrorField(results.errors);
                this.setState({errors: {...this.state.errors, ...results.errors}});
                return;
            }
        }

        this.setState({sectionIdx: sects.length});
    }

    getRD() {
        return this.props.record.collection.recordDefinition;
    }

    validateSection(sect) {
        var errors = {};
        var hasErrors = false;
        for (var f=0; f < sect.fields.length; f++) {
            let fd = sect.fields[f];
            const e = validate(fd, this.state.data[fd.key]);
            errors[fd.key] = e;
            if (e.length > 0) {
                hasErrors = true;
            }
        }

        return {
            errors: errors,
            hasErrors: hasErrors
        };
    }

    validateAllFields() {
        var errorState = {...this.state};
        var hasErrors = false;
        let sects = this.getRD().sections;
        for (var s=0; s < sects.length; s++)
        {
            let results = this.validateSection(sects[s]);
            if (results.hasErrors) {
                errorState.errors = {...errorState.errors, ...results.errors};
                if (!hasErrors) { // Display the editor for the first section w/ an error.
                    hasErrors = true;
                    errorState.sectionIdx = s;
                    // TODO: Consider displaying a notification bubble to explain that we navigated to this section because of a validation error
                }
            }
        }

        return hasErrors ? errorState : null;
    }

    onSubmit = e => {
        e.preventDefault();

        var errorState = this.validateAllFields();

        if (errorState) {
            this.setState(errorState);
        } else {
            this.props.onSubmit(this.state.data);
        }
    }

    getFieldDef(name) {
        const sects = this.getRD().sections;

        for(var s=0; s<sects.length; s++) {
            for (var f=0; f < sects[s].fields.length; f++) {
                let fd = sects[s].fields[f];
                if (fd.key === name) {
                    return fd;
                }
            }
        }

        return null;
    }

    onDataChanged = data => {
        const items = Object.entries(data);
        var errors = {};
        for(var i=0; i < items.length; i++) {
            let key = items[i][0];
            let val = items[i][1];
            let fd = this.getFieldDef(key);

            errors[key] =  validate(fd, val);
        }
        this.setState({data: {...this.state.data, ...data}, errors: {...this.state.errors, ...errors}}, );
    }

    renderHeader(idx) {
        let sects = this.getRD().sections;
        if (idx < sects.length) {
            return <div className="ac-wizard-header">
                <h2 className="ac-section-title">{sects[idx].title}</h2>
            </div>
        } else {
            return <div className="ac-wizard-header">
                    <h2 className="ac-section-title">Almost done...</h2>
                    <span>Does everything look correct?</span>
                </div>
        }
    }

    renderBody(idx) {
        let sects = this.getRD().sections;
        if (idx < sects.length) {
            let s = sects[idx];
            const fields = s.fields.map(f => <Field key={f.key} value={this.state.data[f.key]} fieldDef={f} errors={this.state.errors[f.key]} onValueChanged={this.onDataChanged} ref={(me) => this.fieldRefs[f.key] = me} />);
            return <div className="ac-wizard-body">
                {this.renderIntro(s)}
                {fields}
                </div>
        } else {
             return this.renderReview(sects);
        }
    }

    renderIntro(section) {
        if (section.intro) {
            return <div dangerouslySetInnerHTML={{__html: section.intro}}></div>
        } else {
            return ''
        }
    }

    renderReview(sections) {
        const sects = sections.filter((s) => s.fields.length > 0).map((s, i) =>
            <div key={s.key}>
                <h2 data-idx={i} onClick={this.editSection}>{s.title}</h2>
                {this.renderReviewFields(s)}
                <br />
            </div>
        )

        return <div className="ac-wizard-body ac-review">
            {sects}
        </div>;
    }

    renderFieldValue(f, data) {
        let val = this.state.data[f.key];
        if (val === undefined || val === null) {
            return '';
        }

        if (f.dataType === 'BOOLEAN') {
            val = val ? 'Yes' : 'No';
        }

        if (val instanceof Array){
            try {
                val = val.join(', ');
            } catch {}
        }
        return val.toString();
    }

    renderReviewFields(section) {
        const fields = section.fields.map((f) => (<div key={f.key}><label>{f.label}</label><div>{this.renderFieldValue(f, this.state.data)}</div></div>));
        return <div>{fields}</div>
    }

    renderNav(idx) {
        var nextIdx = idx + 1;
        let sects = this.getRD().sections;
        const steps = sects.map((s, i) => (<span key={s.key} className={i === this.state.sectionIdx ? "ac-step ac-active" : "ac-step"} onClick={this.editSection} data-idx={i} title={s.title}></span>));
        steps.push(<span key={sects.length} className={this.state.sectionIdx >= sects.length ? "ac-step ac-active" : "ac-step"} onClick={this.showReview} title="Review"></span>);
        return <div className="ac-wizard-nav">
                    {idx > 0 ? <button type="button" className="ac-btn" data-idx={idx-1} onClick={this.editSection}>previous</button> : <button type="button" className="ac-btn ac-btn-hidden">previous</button> }
                    <div className="ac-steps">
                        {steps}
                    </div>
                    {(nextIdx < sects.length) ? <button type="button" className="ac-btn ac-btn-primary" data-idx={nextIdx} onClick={this.editSection}>next</button> : (idx >= sects.length) ? <button type="button" className="ac-btn ac-btn-primary" onClick={this.onSubmit}>Yes, Submit</button> : <button type="button" className="ac-btn ac-btn-primary" onClick={this.showReview}>review</button>}
                </div>
    }

    findFirstErrorSection(errors)
    {
        let sects = this.getRD().sections;

        for (var s=0; s < sects.length; s++){
            for (var f=0; f < sects[s].fields.length; f++) {
                if (sects[s].fields[f].key in errors) {
                    return s;
                }
            }
        }

        return sects.length;
    }

    componentWillReceiveProps(props) {
        if (props.errors && props.errors !== this.props.errors){
            let errors = {...this.state.errors, ...props.errors};

            this.setState({
                errors: errors,
                sectionIdx: this.findFirstErrorSection(errors)});
        }
    }

    render() {
        this.fieldRefs = {};
        return <div className="ac-wizard">
            {this.renderHeader(this.state.sectionIdx)}
            {this.renderBody(this.state.sectionIdx)}
            {this.renderNav(this.state.sectionIdx)}
        </div>;
    }

    focusFirstErrorField(errors) {
        if (Object.keys(errors).length > 0 && Object.keys(this.fieldRefs).length > 0) {
            let sect = this.getRD().sections[this.state.sectionIdx];
            for (var f=0; f < sect.fields.length; f++){
                if (errors[sect.fields[f].key].length > 0) {
                    this.fieldRefs[sect.fields[f].key].focus();
                    return true;
                }
            }
        }

        return false;
    }

    focusFirstField() {
        let fields = this.getRD().sections[this.state.sectionIdx].fields;
        if (fields.length > 0){
            const firstField = fields[0].key;
            this.fieldRefs[firstField].focus();
        }
    }

    componentDidMount() {
        if (!this.focusFirstErrorField(this.state.errors)) {
            this.focusFirstField();
        }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (this.state.sectionIdx < this.getRD().sections.length &&
            this.state.sectionIdx !== prevState.sectionIdx) {
            this.focusFirstField();
        }
    }
}

export default Wizard;